import type AttachmentDataProviderItem from 'owa-service/lib/contract/AttachmentDataProviderItem';
import AttachmentDataProviderItemType from 'owa-service/lib/contract/AttachmentDataProviderItemType';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import type AttachmentItemsSortColumn from 'owa-service/lib/contract/AttachmentItemsSortColumn';
import type AttachmentItemsSortOrder from 'owa-service/lib/contract/AttachmentItemsSortOrder';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import type GetAttachmentDataProviderItemsResponse from 'owa-service/lib/contract/GetAttachmentDataProviderItemsResponse';
import handleGetItemsResponse from './handleGetItemsResponse';
import { assertNever } from 'owa-assert';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getMimeTypeForOneDriveItem } from '../utils/getMimeTypeForOneDriveItem';
import {
    oneDriveItemsSkipTokenPagingMetadata,
    OneDriveItemsSkipTokenPagingMetadata,
} from '../types/OneDriveItemsSkipTokenPagingMetadata';
import {
    FileAttachmentDataProviderItemType,
    FolderAttachmentDataProviderItemType,
} from '../constants';
import {
    getOneDriveItems,
    GetOneDriveItemsPagingInfo,
    ONE_DRIVE_CONUSMER_API_URL,
    OneDriveItem,
} from 'owa-fileprovider-services';

export function createFailedAttachmentDataProviderItemsResponse(
    errorCode: AttachmentResultCode
): GetAttachmentDataProviderItemsResponse {
    return {
        TotalItemCount: 0,
        ResultCode: errorCode,
        Items: [],
    };
}

function createAttachmentDataProviderItemForOneDriveItem(
    item: OneDriveItem,
    endpointUrl: string,
    fileProviderId: string,
    providerType: AttachmentDataProviderType
): AttachmentDataProviderItem {
    const [typeOfObject, mimeType, childCount, type, size] = item.folder
        ? [
              FolderAttachmentDataProviderItemType,
              null,
              item.folder.childCount,
              AttachmentDataProviderItemType.Folder,
              0, // This API returns the size of a folder as the sum of the sizes of the files in the folder.
              // We want to set this to 0 instead to be consistent with other APIs/providers.
          ]
        : [
              FileAttachmentDataProviderItemType,
              getMimeTypeForOneDriveItem(item),
              0,
              AttachmentDataProviderItemType.File,
              item.size,
          ];
    return {
        __type: typeOfObject,
        Id: item.id,
        Name: item.name,
        Size: size,
        Thumbnail: {
            Url: item.thumbnails?.[0]?.medium ? item.thumbnails[0].medium.url : null,
        },
        Modified: item.lastModifiedDateTime,
        ModifiedBy: item.lastModifiedBy?.user ? item.lastModifiedBy.user.displayName : null,
        MimeType: mimeType,
        Location: item.webDavUrl,
        ProviderEndpointUrl: endpointUrl,
        ChildCount: childCount,
        AttachmentProviderId: fileProviderId,
        ProviderType: providerType,
        Type: type,
    };
}

function createAttachmentDataProviderItemsResponse(
    items: OneDriveItem[],
    endpointUrl: string,
    fileProviderId: string,
    providerType:
        | AttachmentDataProviderType.OneDrivePro
        | AttachmentDataProviderType.OneDriveConsumer,
    newSkipToken: string | null,
    previousPagingMetadata: OneDriveItemsSkipTokenPagingMetadata | null
): GetAttachmentDataProviderItemsResponse {
    let pagingMetadata: OneDriveItemsSkipTokenPagingMetadata = undefined;

    if (newSkipToken) {
        switch (providerType) {
            case AttachmentDataProviderType.OneDrivePro:
                pagingMetadata = getOneDriveItemsSkipTokenPagingMetadata(
                    items,
                    newSkipToken,
                    previousPagingMetadata
                );
                break;
            case AttachmentDataProviderType.OneDriveConsumer:
                if (isFeatureEnabled('doc-odcgetitems')) {
                    pagingMetadata = getOneDriveItemsSkipTokenPagingMetadata(
                        items,
                        newSkipToken,
                        previousPagingMetadata
                    );
                    break;
                } else {
                    throw new Error(
                        'We do not support skip token based paging for OneDriveConsumer'
                    );
                }
            default:
                assertNever(providerType);
        }
    }

    let totalItemsCount: number = items.length; // The default total items is the length of items we received when there is no page
    if (newSkipToken && pagingMetadata) {
        // If there is a next page then use the paging metadata to get the total
        // count of items fetched till now and we add 1 more to it to indicate there are more items
        totalItemsCount = pagingMetadata.totalItemsFetched + 1;
    } else if (pagingMetadata) {
        // If there is no skip token but there was paging metadata then use the total count from it
        totalItemsCount = pagingMetadata.totalItemsFetched;
    }

    return {
        TotalItemCount: totalItemsCount,
        ResultCode: AttachmentResultCode.Success,
        Items: items.map(item =>
            createAttachmentDataProviderItemForOneDriveItem(
                item,
                endpointUrl,
                fileProviderId,
                providerType
            )
        ),
        PagingMetadata: pagingMetadata,
    };
}

function getOneDriveItemsSkipTokenPagingMetadata(
    items: OneDriveItem[],
    newSkipToken: string | null,
    previousPagingMetadata: OneDriveItemsSkipTokenPagingMetadata | null
): OneDriveItemsSkipTokenPagingMetadata {
    const totalItemsFetched = previousPagingMetadata
        ? previousPagingMetadata.totalItemsFetched + items.length
        : items.length;
    return oneDriveItemsSkipTokenPagingMetadata({
        skipToken: newSkipToken,
        totalItemsFetched: totalItemsFetched,
    });
}

function getResponse(
    errorResponse: Response,
    result: OneDriveItem[],
    endpointUrl: string,
    fileProviderId: string,
    providerType:
        | AttachmentDataProviderType.OneDrivePro
        | AttachmentDataProviderType.OneDriveConsumer,
    skipToken: string | null,
    previousPagingMetadata: OneDriveItemsSkipTokenPagingMetadata | null
): GetAttachmentDataProviderItemsResponse {
    if (result) {
        return handleGetItemsResponse(
            result,
            null /* errorResponse */,
            (items: OneDriveItem[]) =>
                createAttachmentDataProviderItemsResponse(
                    items,
                    endpointUrl,
                    fileProviderId,
                    providerType,
                    skipToken,
                    previousPagingMetadata
                ),
            createFailedAttachmentDataProviderItemsResponse
        );
    } else {
        return handleGetItemsResponse<OneDriveItem[]>(
            null /* result */,
            errorResponse,
            (items: OneDriveItem[]) =>
                createAttachmentDataProviderItemsResponse(
                    items,
                    endpointUrl,
                    fileProviderId,
                    providerType,
                    skipToken,
                    previousPagingMetadata
                ),
            createFailedAttachmentDataProviderItemsResponse
        );
    }
}

export async function getOneDriveProFileProviderItems(
    endpointUrl: string,
    fileProviderId: string,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    pagingMetadata: { metadata: OneDriveItemsSkipTokenPagingMetadata; maxEntries: number } = null
): Promise<GetAttachmentDataProviderItemsResponse> {
    const endpointParsedUrl = new URL(endpointUrl);
    const endpointDomain = `${endpointParsedUrl.protocol}//${endpointParsedUrl.host}`;

    return getOneDriveFileProviderItems(
        endpointUrl,
        endpointDomain,
        AttachmentDataProviderType.OneDrivePro,
        fileProviderId,
        sortColumn,
        sortOrder,
        folderId,
        pagingMetadata
    );
}

export async function getOneDriveConsumerFileProviderItems(
    fileProviderId: string,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    pagingMetadata: { metadata: OneDriveItemsSkipTokenPagingMetadata; maxEntries: number } = null
): Promise<GetAttachmentDataProviderItemsResponse> {
    return getOneDriveFileProviderItems(
        ONE_DRIVE_CONUSMER_API_URL,
        ONE_DRIVE_CONUSMER_API_URL,
        AttachmentDataProviderType.OneDriveConsumer,
        fileProviderId,
        sortColumn,
        sortOrder,
        folderId,
        pagingMetadata
    );
}

async function getOneDriveFileProviderItems(
    endpointUrl: string,
    apiBaseUrl: string,
    providerType:
        | AttachmentDataProviderType.OneDrivePro
        | AttachmentDataProviderType.OneDriveConsumer,
    fileProviderId: string,
    sortColumn: AttachmentItemsSortColumn,
    sortOrder: AttachmentItemsSortOrder,
    folderId?: string,
    pagingMetadata: { metadata: OneDriveItemsSkipTokenPagingMetadata; maxEntries: number } = null
): Promise<GetAttachmentDataProviderItemsResponse> {
    const pagingInfo: GetOneDriveItemsPagingInfo | null = pagingMetadata
        ? {
              maxEntries: pagingMetadata.maxEntries,
              skipToken: pagingMetadata.metadata ? pagingMetadata.metadata.skipToken : null,
          }
        : null;

    const {
        errorResponse,
        result: { items, skipToken },
    } = await getOneDriveItems(
        apiBaseUrl,
        providerType,
        sortColumn,
        sortOrder,
        folderId,
        undefined /* filter */,
        pagingInfo
    );

    const response = getResponse(
        errorResponse,
        items,
        endpointUrl,
        fileProviderId,
        AttachmentDataProviderType.OneDrivePro,
        skipToken,
        pagingMetadata ? pagingMetadata.metadata : null
    );

    return response;
}
